/**
 * BlockEditorContainer - 块编辑器容器
 * 复刻 trial_demo_editor 的渲染方式：
 * - 使用 <div> 而非 <ol>/<li>
 * - 通过 JS 计算 bulletText 和 inline styleConfig
 * - 不依赖 CSS counters / ::before
 */

import React, { useEffect, useRef, useCallback, useState, useMemo } from 'react';
import { copyToClipboard } from '../../../utils/clipboard';
import {
  DragDropContext,
  Droppable,
  Draggable,
  type DropResult,
  type DragUpdate,
  type DragStart,
} from '@hello-pangea/dnd';
import EditableBlock from './EditableBlock';
import { useBlockReducer } from './useBlockReducer';
import type { BlockEditorContainerProps, BlockStyleConfig } from './types';
import type { NoteBlock } from '../../../types';
import { computeBulletText, computeEditableStyleConfig, computeBlockLevelIndices } from './blockStyleUtils';
import { MarqueeSelection } from './MarqueeSelection';
import './blockEditor.css';

function StrictModeDroppable({ children, ...props }: React.ComponentProps<typeof Droppable>) {
  const [enabled, setEnabled] = useState(false);
  useEffect(() => {
    const animation = requestAnimationFrame(() => setEnabled(true));
    return () => { cancelAnimationFrame(animation); };
  }, []);
  if (!enabled) return null;
  return <Droppable {...props}>{children}</Droppable>;
}

export function BlockEditorContainer({
  initialBlocks,
  onBlocksChange,
  scopeClass,
  css,
  levelStyles,
  activeLinkId,
  linkedIds,
  onLinkToggle,
  matchedNoteIds,
  topLinkedId,
}: BlockEditorContainerProps) {
  const [state, dispatch] = useBlockReducer(initialBlocks);
  const lastExternalBlocksRef = useRef<NoteBlock[]>(initialBlocks);
  const isFirstRenderRef = useRef(true);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false;
      return;
    }
    if (initialBlocks !== lastExternalBlocksRef.current) {
      lastExternalBlocksRef.current = initialBlocks;
      dispatch({ type: 'INIT', blocks: initialBlocks });
    }
  }, [initialBlocks, dispatch]);

  const blocksVersionRef = useRef(0);
  useEffect(() => {
    blocksVersionRef.current += 1;
    const version = blocksVersionRef.current;
    if (version === 1) return;

    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(() => {
      lastExternalBlocksRef.current = state.blocks;
      onBlocksChange(state.blocks);
    }, 300);

    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    };
  }, [state.blocks, onBlocksChange]);

  useEffect(() => {
    const handleGlobalKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        dispatch({ type: 'UNDO' });
      }
      if ((e.ctrlKey || e.metaKey) && (e.key === 'y' || (e.key === 'z' && e.shiftKey))) {
        e.preventDefault();
        dispatch({ type: 'REDO' });
      }
    };
    document.addEventListener('keydown', handleGlobalKeyDown);
    return () => document.removeEventListener('keydown', handleGlobalKeyDown);
  }, [dispatch]);

  const levelStyleConfigs = useMemo(() => {
    const configs: BlockStyleConfig[] = [];
    for (const ls of levelStyles) {
      configs[ls.level] = computeEditableStyleConfig(ls);
    }
    return configs;
  }, [levelStyles]);

  const blockLevelIndices = useMemo(
    () => computeBlockLevelIndices(state.blocks),
    [state.blocks]
  );

  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [dropIndicatorIndex, setDropIndicatorIndex] = useState<number | null>(null);
  const dragSourceIndex = useRef<number | null>(null);

  const handleDragStart = useCallback((start: DragStart) => {
    setDraggingId(start.draggableId);
    dragSourceIndex.current = start.source.index;
  }, []);

  const handleDragUpdate = useCallback((update: DragUpdate) => {
    if (update.destination) {
      setDropIndicatorIndex(update.destination.index);
    } else {
      setDropIndicatorIndex(null);
    }
  }, []);

  const handleDragEnd = useCallback((result: DropResult) => {
    setDraggingId(null);
    setDropIndicatorIndex(null);
    dragSourceIndex.current = null;
    if (!result.destination) return;
    if (result.source.index === result.destination.index) return;

    const draggedId = result.draggableId;
    const isMultiDrag = state.selectedBlockIds.length > 1 && state.selectedBlockIds.includes(draggedId);

    if (isMultiDrag) {
      // dnd 的 destination.index 是基于移除拖动元素后的列表
      // 需要转换回原始列表的索引
      const src = result.source.index;
      const dst = result.destination.index;
      const originalDest = dst >= src ? dst + 1 : dst;

      // 检查目标是否在选中区域内，如果是则不移动
      const selectedIndices = state.selectedBlockIds.map(id => state.blocks.findIndex(b => b.id === id)).filter(i => i !== -1);
      const minSel = Math.min(...selectedIndices);
      const maxSel = Math.max(...selectedIndices);
      if (originalDest >= minSel && originalDest <= maxSel + 1) return;

      dispatch({
        type: 'MOVE_SELECTED',
        selectedIds: state.selectedBlockIds,
        destIndex: originalDest,
      });
    } else {
      dispatch({
        type: 'MOVE',
        sourceIndex: result.source.index,
        destIndex: result.destination.index,
      });
    }
  }, [dispatch, state.selectedBlockIds, state.blocks]);

  // 框选回调
  const handleMarqueeSelection = useCallback((ids: string[]) => {
    dispatch({ type: 'SET_SELECTION', ids });
  }, [dispatch]);

  // 跨 block 文本拖选：在文字上按住鼠标拖到另一个 block 时，
  // 切换为 block 级选择（选中整个 block），支持复制/删除等操作
  const textDragStartBlockId = useRef<string | null>(null);
  const isTextDragging = useRef(false);

  useEffect(() => {
    const handleMouseDown = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target.isContentEditable || target.closest('[contenteditable]')) {
        const blockEl = target.closest('[data-block-id]') as HTMLElement | null;
        if (blockEl) {
          textDragStartBlockId.current = blockEl.getAttribute('data-block-id');
          isTextDragging.current = true;
        }
      } else {
        textDragStartBlockId.current = null;
        isTextDragging.current = false;
      }
    };

    const handleMouseMove = (e: MouseEvent) => {
      if (!isTextDragging.current || !textDragStartBlockId.current) return;
      if (e.buttons !== 1) return;

      const target = document.elementFromPoint(e.clientX, e.clientY) as HTMLElement | null;
      if (!target) return;
      const blockEl = target.closest('[data-block-id]') as HTMLElement | null;
      if (!blockEl) return;
      const currentBlockId = blockEl.getAttribute('data-block-id');
      if (!currentBlockId) return;

      // 还在同一个 block 内，让浏览器原生处理
      if (currentBlockId === textDragStartBlockId.current) return;

      // 跨越了 block 边界，切换为 block 级选择
      const startIdx = state.blocks.findIndex(b => b.id === textDragStartBlockId.current);
      const endIdx = state.blocks.findIndex(b => b.id === currentBlockId);
      if (startIdx === -1 || endIdx === -1) return;
      const from = Math.min(startIdx, endIdx);
      const to = Math.max(startIdx, endIdx);
      const ids = state.blocks.slice(from, to + 1).map(b => b.id);

      window.getSelection()?.removeAllRanges();
      dispatch({ type: 'SET_SELECTION', ids });
    };

    const handleMouseUp = () => {
      isTextDragging.current = false;
      textDragStartBlockId.current = null;
    };

    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [state.blocks, dispatch]);

  // 选中 block 的键盘操作：复制、删除
  useEffect(() => {
    if (state.selectedBlockIds.length === 0) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (state.selectedBlockIds.length === 0) return;

      // Ctrl+C / Cmd+C：复制选中 block 的文本
      if ((e.ctrlKey || e.metaKey) && e.key === 'c') {
        const selectedTexts = state.selectedBlockIds
          .map(id => state.blocks.find(b => b.id === id))
          .filter(Boolean)
          .map(b => b!.content)
          .join('\n');
        copyToClipboard(selectedTexts);
        e.preventDefault();
        return;
      }

      // Ctrl+X / Cmd+X：剪切
      if ((e.ctrlKey || e.metaKey) && e.key === 'x') {
        const selectedTexts = state.selectedBlockIds
          .map(id => state.blocks.find(b => b.id === id))
          .filter(Boolean)
          .map(b => b!.content)
          .join('\n');
        copyToClipboard(selectedTexts);
        dispatch({ type: 'DELETE', ids: [...state.selectedBlockIds] });
        e.preventDefault();
        return;
      }

      // Backspace / Delete：删除选中的 block
      if (e.key === 'Backspace' || e.key === 'Delete') {
        dispatch({ type: 'DELETE', ids: [...state.selectedBlockIds] });
        e.preventDefault();
        return;
      }

      // Escape：取消选择
      if (e.key === 'Escape') {
        dispatch({ type: 'SET_SELECTION', ids: [] });
        e.preventDefault();
        return;
      }

      // 普通字符输入：删除选中 block，替换为包含输入字符的新 block
      if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
        e.preventDefault();
        const firstSelectedIdx = state.blocks.findIndex(b => state.selectedBlockIds.includes(b.id));
        const firstBlock = firstSelectedIdx !== -1 ? state.blocks[firstSelectedIdx] : null;
        const level = firstBlock ? firstBlock.level : 1;
        dispatch({
          type: 'REPLACE_SELECTED',
          ids: [...state.selectedBlockIds],
          content: e.key,
          level,
        });
        return;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [state.selectedBlockIds, state.blocks, dispatch]);

  return (
    <>
      <MarqueeSelection onSelectionChange={handleMarqueeSelection} />
      <DragDropContext onDragStart={handleDragStart} onDragUpdate={handleDragUpdate} onDragEnd={handleDragEnd}>
      <StrictModeDroppable droppableId="block-editor">
        {(provided, snapshot) => (
          <div
            className={`droppable-container flex flex-col${snapshot.isDraggingOver ? ' block-editor-dragging' : ''}`}
            ref={provided.innerRef}
            {...provided.droppableProps}
            data-is-background="true"
          >
            {state.blocks.map((block, index) => {
              const levelIdx = block.level - 1;
              const computed = levelStyles[levelIdx];
              const styleConfig = levelStyleConfigs[levelIdx] || levelStyleConfigs[0];
              const bulletText = computed
                ? computeBulletText(computed, blockLevelIndices[index].index, blockLevelIndices[index].counters, levelStyles)
                : '';

              // 多选拖动状态
              const isBlockSelected = state.selectedBlockIds.includes(block.id);
              const isMultiDragging = draggingId !== null && state.selectedBlockIds.length > 1 && state.selectedBlockIds.includes(draggingId);
              const isMultiDragOther = isMultiDragging && isBlockSelected && block.id !== draggingId;
              const multiDragCount = isMultiDragging && block.id === draggingId ? state.selectedBlockIds.length : 0;

              // 计算选中 block 的索引范围，指示线不在选中区域内显示
              let isDropInSelectedRange = false;
              // dnd 的 dropIndicatorIndex 是基于移除拖动元素后的列表，转换回原始索引
              let originalDropIndex: number | null = null;
              if (dropIndicatorIndex !== null && dragSourceIndex.current !== null) {
                originalDropIndex = dropIndicatorIndex >= dragSourceIndex.current
                  ? dropIndicatorIndex + 1
                  : dropIndicatorIndex;
              }

              if (isMultiDragging && originalDropIndex !== null) {
                const selectedIndices = state.selectedBlockIds.map(id => state.blocks.findIndex(b => b.id === id)).filter(i => i !== -1);
                const minSel = Math.min(...selectedIndices);
                const maxSel = Math.max(...selectedIndices);
                isDropInSelectedRange = originalDropIndex >= minSel && originalDropIndex <= maxSel + 1;
              }

              // 指示线：基于原始索引显示
              const showIndicatorBefore = !isDropInSelectedRange
                && draggingId !== null
                && originalDropIndex !== null
                && originalDropIndex === index;

              const showIndicatorAfter = !isDropInSelectedRange
                && draggingId !== null
                && originalDropIndex !== null
                && originalDropIndex === state.blocks.length
                && index === state.blocks.length - 1;

              return (
                <React.Fragment key={block.id}>
                  {showIndicatorBefore && (
                    <div className="block-drop-indicator" />
                  )}
                  <Draggable draggableId={block.id} index={index}>
                    {(dragProvided, dragSnapshot) => (
                      <div
                        ref={dragProvided.innerRef}
                        {...dragProvided.draggableProps}
                        style={{
                          ...dragProvided.draggableProps.style,
                          ...(isMultiDragOther ? { opacity: 0.3 } : {}),
                        }}
                      >
                        <EditableBlock
                          block={block}
                          index={index}
                          isActive={block.id === state.activeBlockId}
                          cursorOffset={block.id === state.activeBlockId ? state.cursorOffset : null}
                          isSelected={isBlockSelected}
                          isDragging={dragSnapshot.isDragging}
                          isMultiDragOther={isMultiDragOther}
                          multiDragCount={multiDragCount}
                          dragHandleProps={dragProvided.dragHandleProps}
                          dispatch={dispatch}
                          blocks={state.blocks}
                          isLinkedActive={linkedIds?.has(block.id)}
                          hasMatch={matchedNoteIds?.has(block.id)}
                          isLineEndpoint={block.id === activeLinkId || block.id === topLinkedId}
                          onLinkToggle={onLinkToggle}
                          bulletText={bulletText}
                          styleConfig={styleConfig}
                        />
                      </div>
                    )}
                  </Draggable>
                  {showIndicatorAfter && (
                    <div className="block-drop-indicator" />
                  )}
                </React.Fragment>
              );
            })}
            {provided.placeholder}
          </div>
        )}
      </StrictModeDroppable>
    </DragDropContext>
    </>
  );
}

export default BlockEditorContainer;
